import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../useAuth";
import {
  LayoutDashboard,
  BarChart2,
  Users,
  UserPlus,
  ClipboardList,
  CheckSquare,
  TrendingUp,
  BookOpen,
  PlusCircle,
  FileText,
  HelpCircle,
  Package,
  Calendar,
  CreditCard,
  FileCheck,
  Bell,
  Settings,
  Shield,
  RefreshCw,
  LogOut,
  ChevronDown,
  GraduationCap,
  MessageSquare,
  PieChart,
  User,
  Layers,
  FilePlus,
  Radio,
  List,
  Grid,
} from "lucide-react";

export default function NavBar() {
  const { clientInfo, user } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);

  const toggleSection = (index) => {
    setActiveSection(activeSection === index ? null : index);
  };

  return (
    <div className="bg-gray-900 h-full min-h-screen shadow-2xl flex flex-col relative w-72 border-r border-gray-800">
      {/* Header Section */}
      <div className="p-5 border-b border-gray-800">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-indigo-600 rounded-lg shadow-lg shadow-indigo-500/30">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div className="flex flex-col">
            <div className="text-white text-lg font-bold tracking-tight">
              Test Setu
            </div>
            <div className="text-gray-500 text-xs font-medium uppercase tracking-wider">
              {user?.userType || "Admin Console"}
            </div>
          </div>
        </div>

        {/* User Info Card */}
        <div className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-inner border border-white/10">
              {user?.name?.charAt(0) || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-semibold truncate">
                {user?.name || "Administrator"}
              </div>
              <div className="text-gray-500 text-xs truncate">
                {user?.email || "admin@setu.com"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <div className="flex-1 px-3 py-4 overflow-y-auto custom-scrollbar">
        <div className="space-y-1">
          {/* GROUP: MAIN MENU */}
          <NavGroupLabel label="Main Menu" />
          <NavSection
            title="Dashboard"
            icon={<LayoutDashboard className="w-4 h-4" />}
            index={0}
            isOpen={activeSection === 0}
            onToggle={() => toggleSection(0)}
            accessibleRoles={["admin"]}
          >
            <NavItem
              title="Overview"
              path=""
              icon={<Grid className="w-4 h-4" />}
              isHovered={hoveredItem === "overview"}
              onHover={setHoveredItem}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Analytics"
              path="analytics"
              icon={<BarChart2 className="w-4 h-4" />}
              isHovered={hoveredItem === "analytics"}
              onHover={setHoveredItem}
              accessibleRoles={["admin"]}
            />
          </NavSection>

          {/* GROUP: ACADEMIC */}
          <NavGroupLabel label="Academic" />
          <NavSection
            title="Students"
            icon={<GraduationCap className="w-4 h-4" />}
            index={1}
            isOpen={activeSection === 1}
            onToggle={() => toggleSection(1)}
            accessibleRoles={["admin"]}
          >
            <NavItem
              title="Register Student"
              path="studentRegistration"
              icon={<UserPlus className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "studentRegistration"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Students List"
              path="studentsList"
              icon={<List className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "studentsList"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Attendance"
              path="attendance"
              icon={<CheckSquare className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "attendance"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Attendance List"
              path="attendanceList"
              icon={<ClipboardList className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "attendanceList"}
              accessibleRoles={["admin"]}
            />
          </NavSection>

          <NavSection
            title="Teachers"
            icon={<Users className="w-4 h-4" />}
            index={2}
            isOpen={activeSection === 2}
            onToggle={() => toggleSection(2)}
            accessibleRoles={["admin"]}
          >
            <NavItem
              title="Teachers List"
              path="teachers"
              icon={<List className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "teachers"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Add Teacher"
              path="registerTeacher"
              icon={<UserPlus className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "registerTeacher"}
              accessibleRoles={["admin"]}
            />
          </NavSection>

          {/* Moved Batches Here */}
          <NavSection
            title="Courses & Batches"
            icon={<Layers className="w-4 h-4" />}
            index={3}
            isOpen={activeSection === 3}
            onToggle={() => toggleSection(3)}
            accessibleRoles={["admin"]}
          >
            <NavItem
              title="Courses"
              path="courses"
              icon={<BookOpen className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "courses"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Batches"
              path="batches"
              icon={<Users className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "batches"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Create Batch"
              path="createBatch"
              icon={<PlusCircle className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "createBatch"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Schedule"
              path="schedule"
              icon={<Calendar className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "schedule"}
              accessibleRoles={["admin"]}
            />
          </NavSection>

          {/* GROUP: LEARNING */}
          <NavGroupLabel label="Learning" />
          <NavSection
            title="Content"
            icon={<BookOpen className="w-4 h-4" />}
            index={4}
            isOpen={activeSection === 4}
            onToggle={() => toggleSection(4)}
            accessibleRoles={["admin"]}
          >
            <NavItem
              title="Subjects"
              path="subjectsList"
              icon={<Layers className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "subjectsList"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Add Subject"
              path="addSubject"
              icon={<PlusCircle className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "addSubject"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Chapters"
              path="chaptersList"
              icon={<BookOpen className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "chaptersList"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Add Chapter"
              path="addChapter"
              icon={<PlusCircle className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "addChapter"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Topics"
              path="topicsList"
              icon={<List className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "topicsList"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Add Topic"
              path="addTopics"
              icon={<PlusCircle className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "addTopics"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Study Notes"
              path="notesList"
              icon={<FileText className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "notesList"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Add Notes"
              path="addNotes"
              icon={<FilePlus className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "addNotes"}
              accessibleRoles={["admin"]}
            />
          </NavSection>

          <NavSection
            title="Assessments"
            icon={<ClipboardList className="w-4 h-4" />}
            index={5}
            isOpen={activeSection === 5}
            onToggle={() => toggleSection(5)}
            accessibleRoles={["admin"]}
          >
            <NavItem
              title="Questions Bank"
              path="questionsList"
              icon={<HelpCircle className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "questionsList"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Add Question"
              path="addNewQuestion"
              icon={<PlusCircle className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "addNewQuestion"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Bulk Upload"
              path="addQuestionsInBulk"
              icon={<Package className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "addQuestionsInBulk"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Test Series"
              path="mockTestSeries"
              icon={<List className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "mockTestSeries"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Create Test"
              path="prepareNewTestSeries"
              icon={<PlusCircle className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "prepareNewTestSeries"}
              accessibleRoles={["admin"]}
            />
          </NavSection>

          {/* GROUP: ADMINISTRATION */}
          <NavGroupLabel label="Administration" />
          <NavSection
            title="Fees & Finance"
            icon={<CreditCard className="w-4 h-4" />}
            index={6}
            isOpen={activeSection === 6}
            onToggle={() => toggleSection(6)}
            accessibleRoles={["admin"]}
          >
            <NavItem
              title="Fee Structure"
              path="feeStructure"
              icon={<List className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "feeStructure"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Payments"
              path="payments"
              icon={<CreditCard className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "payments"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Invoices"
              path="invoices"
              icon={<FileCheck className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "invoices"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Reports"
              path="feeReports"
              icon={<PieChart className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "feeReports"}
              accessibleRoles={["admin"]}
            />
          </NavSection>

          <NavSection
            title="Communication"
            icon={<MessageSquare className="w-4 h-4" />}
            index={7}
            isOpen={activeSection === 7}
            onToggle={() => toggleSection(7)}
            accessibleRoles={["admin"]}
          >
            <NavItem
              title="Announcements"
              path="announcements"
              icon={<Radio className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "announcements"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Notifications"
              path="notifications"
              icon={<Bell className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "notifications"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Daily Updates"
              path="dailyUpdatesList"
              icon={<List className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "dailyUpdatesList"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Add Update"
              path="addDailyUpdate"
              icon={<PlusCircle className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "addDailyUpdate"}
              accessibleRoles={["admin"]}
            />
          </NavSection>

          {/* GROUP: ANALYTICS */}
          <NavGroupLabel label="Analytics" />
          <NavSection
            title="Reports"
            icon={<PieChart className="w-4 h-4" />}
            index={8}
            isOpen={activeSection === 8}
            onToggle={() => toggleSection(8)}
            accessibleRoles={["admin"]}
          >
            <NavItem
              title="Performance"
              path="performanceReports"
              icon={<TrendingUp className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "performanceReports"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Attendance"
              path="attendanceReports"
              icon={<BarChart2 className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "attendanceReports"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Finance"
              path="feeReports"
              icon={<CreditCard className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "feeReports"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Custom Reports"
              path="customReports"
              icon={<FileText className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "customReports"}
              accessibleRoles={["admin"]}
            />
          </NavSection>

          {/* GROUP: SYSTEM */}
          <NavGroupLabel label="System" />
          <NavSection
            title="Settings"
            icon={<Settings className="w-4 h-4" />}
            index={9}
            isOpen={activeSection === 9}
            onToggle={() => toggleSection(9)}
            accessibleRoles={["admin"]}
          >
            <NavItem
              title="General"
              path="generalSettings"
              icon={<Settings className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "generalSettings"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="User Roles"
              path="userRoles"
              icon={<Users className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "userRoles"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="App Updates"
              path="appUpdateManager"
              icon={<RefreshCw className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "appUpdateManager"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Types"
              path="typeManager"
              icon={<Layers className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "typeManager"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Categories"
              path="categoryManager"
              icon={<List className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "categoryManager"}
              accessibleRoles={["admin"]}
            />
          </NavSection>

          <NavSection
            title="Profile"
            icon={<User className="w-4 h-4" />}
            index={10}
            isOpen={activeSection === 10}
            onToggle={() => toggleSection(10)}
            accessibleRoles={["admin", "user"]}
          >
            <NavItem
              title="My Profile"
              path="profile"
              icon={<User className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "profile"}
              accessibleRoles={["admin", "user"]}
            />
            <NavItem
              title="Account Settings"
              path="accountSettings"
              icon={<Shield className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "accountSettings"}
              accessibleRoles={["admin", "user"]}
            />
          </NavSection>

          {/* GROUP: QUIZ APP */}
          <NavGroupLabel label="Apps" />
          <NavSection
            title="Quiz App Management"
            icon={<Layers className="w-4 h-4" />}
            index={11}
            isOpen={activeSection === 11}
            onToggle={() => toggleSection(11)}
            accessibleRoles={["admin"]}
          >
            <NavItem
              title="Subject Management"
              path="quiz-subjects"
              icon={<BookOpen className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "quiz-subjects"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Topic Management"
              path="quiz-topics"
              icon={<List className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "quiz-topics"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Questions Management"
              path="quiz-questions"
              icon={<HelpCircle className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "quiz-questions"}
              accessibleRoles={["admin"]}
            />
            <NavItem
              title="Cloud Messaging"
              path="quiz-notifications"
              icon={<Bell className="w-4 h-4" />}
              onHover={setHoveredItem}
              isHovered={hoveredItem === "quiz-notifications"}
              accessibleRoles={["admin"]}
            />
          </NavSection>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-800 bg-gray-900">
        <div className="flex items-center justify-between text-gray-500 text-xs font-medium">
          <span>v2.0.1</span>
          <button className="hover:text-red-400 transition-colors flex items-center gap-1">
            <LogOut className="w-3 h-3" /> Logout
          </button>
        </div>
      </div>
    </div>
  );
}

// Group Label Component
const NavGroupLabel = ({ label }) => (
  <div className="px-3 pb-2 pt-4 first:pt-0">
    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1">
      {label}
    </div>
  </div>
);

// Compact Section Component (Updated styling)
const NavSection = ({
  title,
  icon,
  children,
  isOpen,
  onToggle,
  accessibleRoles,
  index,
}) => {
  const { user } = useAuth();
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeight(isOpen ? `${contentRef.current.scrollHeight}px` : "0px");
    }
  }, [isOpen]);

  if (!accessibleRoles.includes(user?.userType)) return null;

  return (
    <div className="rounded-xl overflow-hidden mb-0.5">
      <button
        onClick={onToggle}
        className={`w-full px-3 py-2.5 text-left flex items-center justify-between transition-all duration-200 ${
          isOpen
            ? "bg-gray-800 text-indigo-400"
            : "text-gray-400 hover:bg-gray-800 hover:text-gray-200"
        }`}
      >
        <div className="flex items-center gap-3">
          {icon}
          <span className="font-medium text-sm tracking-wide">{title}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-300 ${
            isOpen ? "rotate-180 text-indigo-400" : "text-gray-600"
          }`}
        />
      </button>

      <div
        ref={contentRef}
        style={{ maxHeight }}
        className="transition-all duration-300 ease-in-out overflow-hidden"
      >
        <div className="pl-3 pr-2 py-1 space-y-1 bg-gray-900/50">
          {children}
        </div>
      </div>
    </div>
  );
};

// Compact NavItem Component
const NavItem = ({
  title,
  path,
  icon,
  onHover,
  isHovered,
  accessibleRoles = [],
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname?.split("/")?.[2];
  const isActive = pathname === path || (path === "" && pathname === undefined);

  const handleClick = () => {
    navigate(path);
  };

  const handleMouseEnter = () => {
    onHover?.(path);
  };

  const handleMouseLeave = () => {
    onHover?.(null);
  };

  if (!accessibleRoles.includes(user?.userType)) return null;

  return (
    <button
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`
        w-full text-left py-2 px-3 rounded-lg transition-all duration-200 flex items-center gap-3 group
        ${
          isActive
            ? "bg-indigo-600 text-white shadow-md shadow-indigo-500/20"
            : "text-gray-400 hover:text-white hover:bg-gray-800"
        }
      `}
    >
      <span
        className={`transition-transform duration-200 ${
          isActive || isHovered ? "scale-110" : "scale-100"
        }`}
      >
        {icon}
      </span>
      <span className="text-sm font-medium">{title}</span>
      {isActive && (
        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
      )}
    </button>
  );
};
